import React, { useState } from "react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { storage, functions, db } from "../lib/firebase";
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs } from "firebase/firestore";
import { toast } from "react-hot-toast";

interface ImageUploadProps {
  onUpload: (url: string) => void;
  currentphotoUrl?: string;
  kitchenId?: string; // To show kitchen-specific images first
  dishName?: string;
  description?: string;
  kitchenName?: string;
}

interface AiOption {
  photoUrl: string;
  model?: string;
  label?: string;
}

interface ExistingImage {
  url: string;
  name: string;
  kitchenId?: string;
  uploadedAt?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onUpload,
  currentphotoUrl = "",
  kitchenId,
  dishName,
  description,
  kitchenName,
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [generating, setGenerating] = useState(false);
  const [aiOptions, setAiOptions] = useState<AiOption[]>([]);
  const [aiRemaining, setAiRemaining] = useState<number | null>(null);

  // Fetch existing images from all menus
  const { data: existingImages = [], isLoading: loadingImages } = useQuery(
    ["existingMenuImages"],
    async () => {
      const images: ExistingImage[] = [];

      // Fetch from all menu documents
      const menuSnapshot = await getDocs(collection(db, "menus"));

      menuSnapshot.docs.forEach((docSnap) => {
        const menuData = docSnap.data();
        const currentKitchenId = docSnap.id;
        const menuItemsObj = menuData.menuItems || {};

        // Extract images from menu items
        Object.entries(menuItemsObj).forEach(
          ([itemName, itemObj]: [string, any]) => {
            // Main item image
            if (itemObj.photoUrl) {
              images.push({
                url: itemObj.photoUrl,
                name: itemName,
                kitchenId: currentKitchenId,
              });
            }

            // Images from customizations (recursive)
            const extractCustomizationImages = (
              customizations: any[],
              parentName: string
            ) => {
              if (!customizations) return;
              customizations.forEach((level) => {
                level.options?.forEach((opt: any) => {
                  if (opt.photoUrl) {
                    images.push({
                      url: opt.photoUrl,
                      name: `${parentName} - ${opt.name}`,
                      kitchenId: currentKitchenId,
                    });
                  }
                  if (opt.customizations) {
                    extractCustomizationImages(
                      opt.customizations,
                      `${parentName} - ${opt.name}`
                    );
                  }
                });
              });
            };

            if (itemObj.customizations) {
              extractCustomizationImages(itemObj.customizations, itemName);
            }
          }
        );
      });

      // Remove duplicates by URL
      const uniqueImages = Array.from(
        new Map(images.map((img) => [img.url, img])).values()
      );

      return uniqueImages;
    },
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Sort images: current kitchen first, then others
  const sortedImages = React.useMemo(() => {
    if (!kitchenId) return existingImages;

    const currentKitchenImages = existingImages.filter(
      (img) => img.kitchenId === kitchenId
    );
    const otherImages = existingImages.filter(
      (img) => img.kitchenId !== kitchenId
    );

    return [...currentKitchenImages, ...otherImages];
  }, [existingImages, kitchenId]);

  // Filter images based on search
  const filteredImages = sortedImages.filter((img) =>
    img.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    // Check file type
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }

    setUploading(true);
    const timestamp = Date.now();
    const storageRef = ref(storage, `menu_images/${timestamp}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(Math.round(pct));
      },
      (error) => {
        console.error("Upload error:", error);
        const msg = String((error as any)?.message || error || "");
        const code = String((error as any)?.code || "");
        if (
          /billing|402|quota|disabled|closed|unauthorized|permission/i.test(
            msg + code
          )
        ) {
          alert(
            "Image upload failed (Storage). Check Firebase Storage rules and that Blaze billing is enabled. You can still pick an existing photo from the gallery (/eggstacy, /host, /images)."
          );
        } else {
          alert(
            `Upload failed: ${code || msg || "unknown error"}. Try gallery or another image.`
          );
        }
        setUploading(false);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        onUpload(url);
        setUploading(false);
        setProgress(0);
        setShowGallery(false);
      }
    );
  };

  const handleSelectExisting = (url: string) => {
    onUpload(url);
    setShowGallery(false);
  };

  const handleGenerateAI = async () => {
    const name = dishName?.trim();
    if (!name) {
      toast.error("Enter a dish name before generating an image.");
      return;
    }
    if (generating || uploading) return;

    setGenerating(true);
    try {
      const generateMenuImage = httpsCallable(functions, "generateMenuImage", {
        timeout: 170000,
      });
      const result = await generateMenuImage({
        dishName: name,
        description: description?.trim() || undefined,
        kitchenId: kitchenId || undefined,
        kitchenName: kitchenName?.trim() || undefined,
      });
      const data = (result.data || {}) as {
        options?: AiOption[];
        photoUrl?: string;
        remainingToday?: number;
      };
      const options: AiOption[] = Array.isArray(data.options) && data.options.length
        ? data.options.filter((o) => o?.photoUrl)
        : data.photoUrl
          ? [{ photoUrl: data.photoUrl }]
          : [];
      if (!options.length) {
        throw new Error("No image returned");
      }
      setAiRemaining(
        typeof data.remainingToday === "number" ? data.remainingToday : null
      );
      setAiOptions(options);
      setShowGallery(false);
    } catch (err: any) {
      console.error("AI generate error:", err);
      const code = err?.code || "";
      const msg =
        err?.message ||
        err?.details ||
        "Could not generate image. Please try again.";
      // Firebase callable errors often look like "functions/permission-denied"
      const readable = String(msg)
        .replace(/^Firebase:\s*/i, "")
        .replace(/^functions\//i, "");
      if (/unauthenticated|permission-denied/i.test(code + readable)) {
        toast.error("You need chef or admin access to generate images.");
      } else if (/limit reached|busy today/i.test(readable)) {
        toast.error(readable.slice(0, 160));
      } else if (/resource-exhausted|quota/i.test(code + readable)) {
        toast.error("AI quota exceeded. Try again later.");
      } else if (/failed-precondition|not configured/i.test(code + readable)) {
        toast.error("AI image generation is not configured yet.");
      } else {
        toast.error(readable.slice(0, 160) || "Image generation failed.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handlePickAiOption = (url: string) => {
    onUpload(url);
    setAiOptions([]);
    toast.success("Photo selected");
  };

  const canGenerate = Boolean(dishName?.trim()) && !generating && !uploading;

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center flex-wrap">
        <button
          type="button"
          onClick={() => setShowGallery(!showGallery)}
          className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition font-medium"
        >
          {showGallery ? "Hide Gallery" : "Choose from Gallery"}
        </button>

        <span className="text-gray-500">or</span>

        <label className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition cursor-pointer font-medium">
          Upload New
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading || generating}
            className="hidden"
          />
        </label>

        <span className="text-gray-500">or</span>

        <button
          type="button"
          onClick={handleGenerateAI}
          disabled={!canGenerate}
          title={
            dishName?.trim()
              ? "Generate a menu photo with AI"
              : "Enter a dish name first"
          }
          className={`px-4 py-2 rounded-lg font-medium transition ${
            canGenerate
              ? "bg-violet-600 text-white hover:bg-violet-700"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
        >
          {generating ? "Generating…" : "Generate with AI"}
        </button>
      </div>

      {generating && (
        <div className="flex items-center gap-2 text-sm text-violet-700">
          <span className="inline-block h-4 w-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          Creating photo options for “{dishName?.trim()}”… (up to a minute)
        </div>
      )}

      {/* AI options picker — bottom sheet on mobile, centered on desktop */}
      {aiOptions.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Pick a photo</h3>
                <p className="text-xs text-gray-500">
                  Tap the one that looks most like your “{dishName?.trim()}”.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAiOptions([])}
                className="text-3xl leading-none text-gray-500 hover:text-gray-800 px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 px-4 pb-3">
              {aiOptions.map((opt, idx) => (
                <button
                  type="button"
                  key={opt.photoUrl}
                  onClick={() => handlePickAiOption(opt.photoUrl)}
                  className="relative rounded-xl overflow-hidden border-2 border-gray-200 hover:border-violet-600 focus:border-violet-600 focus:outline-none active:scale-[0.98] transition"
                >
                  <img
                    src={opt.photoUrl}
                    alt={`Option ${idx + 1}`}
                    className="w-full aspect-square object-cover bg-gray-100"
                  />
                  {opt.label && (
                    <span className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-[10px] px-1.5 py-0.5 rounded">
                      {opt.label}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 px-4 pb-4">
              <span className="text-xs text-gray-500">
                {aiRemaining !== null ? `${aiRemaining} more tries today` : ""}
              </span>
              <button
                type="button"
                onClick={() => {
                  setAiOptions([]);
                  handleGenerateAI();
                }}
                disabled={generating || aiRemaining === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:opacity-50"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {uploading && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-orange-600 h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
          <p className="text-sm text-gray-600 mt-1">{progress}% uploaded</p>
        </div>
      )}

      {currentphotoUrl && (
        <div className="mt-2">
          <p className="text-sm text-gray-600 mb-1">Current image:</p>
          <img
            src={currentphotoUrl}
            alt="Current"
            className="h-20 w-20 object-cover rounded-lg border-2 border-orange-300"
          />
        </div>
      )}

      {/* Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-800">
                  Select Existing Image
                </h3>
                <button
                  onClick={() => setShowGallery(false)}
                  className="text-3xl text-gray-600 hover:text-gray-800"
                >
                  ×
                </button>
              </div>

              {/* Search */}
              <input
                type="text"
                placeholder="Search images by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Gallery Grid */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingImages ? (
                <div className="flex justify-center items-center py-20">
                  <div className="text-gray-500">Loading images...</div>
                </div>
              ) : filteredImages.length === 0 ? (
                <div className="text-center py-20 text-gray-500">
                  <p className="text-lg mb-2">No images found</p>
                  <p className="text-sm">
                    Upload your first image to start building your gallery
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredImages.map((img, idx) => (
                    <div
                      key={`${img.url}_${idx}`}
                      onClick={() => handleSelectExisting(img.url)}
                      className="group relative cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-orange-500 transition-all hover:shadow-lg"
                    >
                      <img
                        src={img.url}
                        alt={img.name}
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center">
                        <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-semibold">
                          Select
                        </span>
                      </div>
                      <div className="p-2 bg-white">
                        <p
                          className="text-xs text-gray-700 truncate"
                          title={img.name}
                        >
                          {img.name}
                        </p>
                        {img.kitchenId === kitchenId && (
                          <span className="text-xs text-orange-600 font-semibold">
                            Your Kitchen
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600 text-center">
                {filteredImages.length} image(s) available • Click to select
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
